import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import api from "@/api/axios";
import { API, ym } from "@/App";
import { MonthPicker } from "@/components/month-picker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, Download } from "lucide-react";

const inrNum = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

export default function SalaryReports() {
  const [month, setMonth] = useState(ym(new Date()));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!month) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`${API}/reports/salary`, { params: { month } })
      .then((res) => {
        if (!cancelled) setReport(res.data.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch salary report:", err);
        toast.error(err?.response?.data?.message || "Could not fetch salary report.");
        setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month]);

  const downloadCsv = async () => {
    if (!month) return;
    setDownloading(true);
    try {
      const res = await api.get(`${API}/reports/salary`, {
        params: { month, format: "csv" },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Salary_Summary_${month}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download salary report:", err);
      toast.error(err?.response?.data?.message || "Could not download salary report.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link
            to="/reports"
            className="mb-1 flex items-center gap-1 text-sm text-muted-foreground hover:underline"
          >
            <ChevronLeft size={14} /> Reports
          </Link>
          <h1>Salary Reports</h1>
          <p>Monthly, employee-wise salary summary with logged vs. QuickTime hours.</p>
        </div>
      </div>

      <Card className="mt-6">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full sm:w-56">
            <CardTitle className="mb-2 text-sm font-medium">Month</CardTitle>
            <MonthPicker value={month} onChange={setMonth} />
          </div>
          <Button onClick={downloadCsv} disabled={downloading || loading || !report?.rows?.length}>
            <Download size={14} className="mr-1" />
            {downloading ? "Downloading..." : "Download CSV"}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading...</p>
          ) : !report?.rows?.length ? (
            <p className="text-sm text-muted-foreground">No salary data found for this month.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Logged Hours</TableHead>
                    <TableHead>QuickTime Hours</TableHead>
                    <TableHead>Variance</TableHead>
                    <TableHead>Reimbursement (INR)</TableHead>
                    <TableHead>Salary (INR)</TableHead>
                    <TableHead>Payable (INR)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.rows.map((row) => (
                    <TableRow key={row.person}>
                      <TableCell>{row.person}</TableCell>
                      <TableCell>{row.days}</TableCell>
                      <TableCell>{row.logged_hours}</TableCell>
                      <TableCell>{row.quicktime_hours}</TableCell>
                      <TableCell>{row.variance}</TableCell>
                      <TableCell>{inrNum(row.reimbursement)}</TableCell>
                      <TableCell>{inrNum(row.salary)}</TableCell>
                      <TableCell>{inrNum(row.payable)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-semibold">
                    <TableCell>TOTAL ({report.totals.count})</TableCell>
                    <TableCell colSpan={4} />
                    <TableCell>{inrNum(report.totals.reimbursement)}</TableCell>
                    <TableCell>{inrNum(report.totals.salary)}</TableCell>
                    <TableCell>{inrNum(report.totals.payable)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
